import {Component, effect, inject, input, Input, model, OnDestroy, OnInit, PLATFORM_ID, signal, untracked} from '@angular/core';
import {LoadingSpinnerComponent} from "../../../../core/components/loading-spinner/loading-spinner.component";
import {TranslationService} from "../../../../core/services/translation.service";
import {ApplyIfNotEmptyDirective} from "../../../../core/directives/apply-if-not-empty.directive";
import {MatStepper} from "@angular/material/stepper";
import {OfferCardComponent} from "../../offer-card/offer-card.component";
import {Offer} from "../../../../data/models/offer.model";
import {isDigital} from "../../../../core/utils/offer-utils";
import {
  bonusAddressTranslationKey,
  configFlag,
  CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS
} from "../../../../core/utils/digital-order-address";
import {OfferPrice} from "../../../../data/models/price.model";
import {SafeHtml} from "@angular/platform-browser";
import {HouseNoPipe} from "../../../../core/pipes/houseno.pipe";
import {OrderResultModel} from "../../../../data/models/orderResult.model";
import {DatePipe, DecimalPipe, isPlatformBrowser, NgClass} from "@angular/common";
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {BankAccountPipe} from "../../../../core/pipes/bank-account.pipe";
import {GtmEnum} from "../../../../core/models/gtm.enum";
import {NavigationCommand, NavigationService} from "../../../../data/services/navigation.service";
import {HupSubscriptionsService} from "../../../../data/services/hup-subscriptions.service";
import {LoginService} from "../../../../core/components/login/login.service";
import {OrderTrackingService} from "../../../../core/services/order-tracking.service";
import {SnackbarService} from "../../../../core/services/snackbar.service";
import {UserData} from "../../../../data/models/user-data.model";
import {ConfigService} from "../../../../core/services/config.service";
import {ScriptLoaderService} from "../../../../core/services/script-loader.service";
import {AddressFormValue, OrderState} from "../../../../data/models/subscription-order.model";
import {hasOrderAddress} from "../../../../core/utils/bonus-address-resolution";
import {isAddressFormValueEmpty} from "../../../../core/utils/external-address-merge";
import {
  HupSubscriptionAddressComponent
} from "../../../hup-subscription/hup-subscription-detail/hup-subscription-address/hup-subscription-address.component";
import {FormBuilder, FormGroup} from "@angular/forms";
import {PaymentProviderService} from "../../../../core/services/payment-provider.service";
import {ValidationPattern} from "../../../../core/models/ValidationPattern";
import {Subscription} from "rxjs";
import {MatIcon} from "@angular/material/icon";
import {DisclaimerMode, toDisclaimerMode} from "../../../../core/models/disclaimer-mode.enum";

@Component({
  selector: 'app-summary-step',
  imports: [
    LoadingSpinnerComponent,
    ApplyIfNotEmptyDirective,
    OfferCardComponent,
    NgClass,
    HupSubscriptionAddressComponent,
    MatIcon
  ],
  templateUrl: './summary-step.component.html',
  styleUrl: './summary-step.component.css'
})
export class SummaryStepComponent implements OnInit, OnDestroy {

  //Services
  private orderProcessService = inject(OrderProcessingService);
  protected translationService = inject(TranslationService);
  protected navigationService = inject(NavigationService);
  protected subscriptionService = inject(HupSubscriptionsService);
  protected loginService = inject(LoginService);
  private orderTrackingService = inject(OrderTrackingService);
  private configService = inject(ConfigService);
  private scriptLoader = inject(ScriptLoaderService);
  private paymentProviderService = inject(PaymentProviderService);

  private snackBar = inject(SnackbarService);
  private platformId = inject(PLATFORM_ID);

  //Pipes
  private houseNoPipe = inject(HouseNoPipe);
  private datePipe = inject(DatePipe);
  private bankAccountPipe = inject(BankAccountPipe);
  private decimalPipe = inject(DecimalPipe);

  protected clientNoUrl = input<string>(null);
  public userData = input<UserData>();
  public offer = input<Offer>();

  isDigitalOffer(): boolean {
    return isDigital(this.offer()?.offerType);
  }

  /**
   * Darf die Rechnungsadresse hier ueberhaupt geaendert werden - also: gibt es den Stift und kann
   * sich eine Maske oeffnen?
   *
   * Nein, wenn der Adress-Step die Rechnungsadresse bereits abgefragt hat: Eine einmal erfasste
   * Adresse wird hier nur noch angezeigt, nicht ein zweites Mal abgefragt.
   *
   * Nein auch beim Bestandskunden ohne Online-Zahlart: Seine Rechnungsadresse kommt aus seinem Konto
   * und wird in der Uebersicht nur angezeigt. Bei einer Online-Zahlart bleibt sie aenderbar, weil
   * dort die Anschrift des Zahlungsanbieters gilt und korrigierbar sein muss.
   */
  protected isBillingAddressEditable(): boolean {
    if (this.billingAddressAskedInAddressStep()) {
      return false;
    }
    const isExistingCustomer = this.userData()?.userBackendId > 0;
    return !isExistingCustomer || this.showOnlinePayment();
  }
  protected offerPrice = model<OfferPrice[]>();

  protected calculatingPrice = signal(false);
  protected creatingOrder = signal(false);
  protected hasPriceCalculationError = signal(false);

  protected selectedDeliveryAddress = signal(null);
  protected selectedBillingAddress = signal(null);
  protected selectedBonusAddress = signal(null);
  protected selectedPaymentMethod = signal(null);
  protected selectedPaymentFrequency = signal(null);
  protected selectedStartDate = signal(null);

  protected showOnlinePayment = input(false);
  protected disclaimerMode: DisclaimerMode = DisclaimerMode.SEPARATE_CHECKBOXES;

  protected paymentInfoBlock = signal('');
  protected durationInfoBlock = signal('');
  protected billingInfoBlock = signal('');
  protected deliveryInfoBlock = signal('');
  protected bonusInfoBlock = signal('');
  protected priceInfoBlock = signal('');

  protected needBillingAddress = signal(false);
  protected showBillingAddressEditor = signal(false);
  protected optionalBillingAddressValid = signal(true);

  /** Hat der Adress-Step die Rechnungsadresse schon abgefragt? Dann hier nur noch anzeigen. */
  protected billingAddressAskedInAddressStep = signal(false);
  /** Erscheint die Praemienadresse unter dem Titel der Lieferadresse? */
  protected bonusAddressIsDeliveryAddress = signal(false);

  /** Hat der Nutzer die Maske selbst ueber den Stift auf- bzw. zugeklappt? Dann nie automatisch nachfassen. */
  private billingEditorOpenedByUser = false;

  /**
   * Die Rechnungsadresse, die prefillBillingAddressFromBonus zuletzt in den Bestell-State geschrieben
   * hat. Steht sie dort unveraendert, darf sie einer geaenderten Praemienadresse folgen.
   */
  private billingAddressFromBonus: AddressFormValue;

  /** Der zuletzt gesehene Bestell-State - fuer die Neubewertung, wenn sich nur die Zahlart-Art aendert. */
  private lastOrderState: OrderState;

  /**
   * Die Wahl einer Online-Zahlart erreicht diesen Schritt als Input, nicht ueber den Bestell-State - und
   * zwar erst mit dem naechsten Change Detection, also NACH der State-Emission derselben Zahlart-Wahl.
   * Ohne eigene Neubewertung bliebe die von selbst geoeffnete Maske deshalb offen, bis der State das
   * naechste Mal emittiert; dann steht dort aber schon die Adresse des Zahlungsanbieters.
   */
  private readonly onlinePaymentChanged = effect(() => {
    this.showOnlinePayment();
    untracked(() => {
      if (this.lastOrderState) {
        this.updateBillingAddressRequest(this.lastOrderState);
      }
    });
  });

  private formBuilder = new FormBuilder();
  billingAddressFormGroup: FormGroup;
  private addressFormSubscriptions = new Subscription();
  private stateSubscriptions = new Subscription();

  errorsArr: string[] = [];

  @Input() stepper!: MatStepper;

  ngOnInit(): void {
    this.disclaimerMode = toDisclaimerMode(this.configService.getConfig('offer.privacyDisclaimerAgbInOneField')?.value);
    this.bonusAddressIsDeliveryAddress.set(
      configFlag(this.configService.getConfig(CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS), false));
    this.buildAddressGroups();
    this.observeAddressValidity();
    this.observeChanges();
  }

  ngOnDestroy(): void {
    this.addressFormSubscriptions.unsubscribe();
    this.stateSubscriptions.unsubscribe();
  }

  private observeAddressValidity(): void {
    this.observeSingleAddressValidity(this.billingAddressGroup);

    this.scheduleOptionalBillingAddressValidityUpdate();
  }

  private observeSingleAddressValidity(group: FormGroup): void {
    const showAddressControl = group.get('showAddress');
    if (showAddressControl) {
      this.addressFormSubscriptions.add(
        showAddressControl.valueChanges.subscribe(() => {
          this.scheduleOptionalBillingAddressValidityUpdate();
        })
      );
    }

    this.addressFormSubscriptions.add(
      group.statusChanges.subscribe(() => {
        this.scheduleOptionalBillingAddressValidityUpdate();
      })
    );
  }

  private scheduleOptionalBillingAddressValidityUpdate(): void {
    setTimeout(() => {
      const needsBillingAddress = this.needBillingAddress();
      this.optionalBillingAddressValid.set(!(needsBillingAddress || this.showBillingAddressEditor()) || this.billingAddressGroup.valid);
    });
  }

  private observeChanges() {
    //Änderungen abonnieren, damit später die Informationen in der UI aktualisiert werden
    this.stateSubscriptions.add(this.orderProcessService.getOrderState().subscribe(state => {
      // Vor allem anderen: Ob die Rechnungsadresse hier ueberhaupt noch abgefragt wird, haengt daran
      // (isBillingAddressEditable) - und updateBillingAddressRequest weiter unten wertet es aus.
      this.billingAddressAskedInAddressStep.set(!!state.billingAddressAskedInAddressStep);

      // Der Push in den Bestell-State emittiert synchron einen neuen State, der diesen Handler
      // vollstaendig durchlaeuft. Mit dem alten State weiterzumachen, wuerde die Maske wieder
      // aufklappen, als gaebe es keine Adresse.
      if (this.prefillBillingAddressFromBonus(state)) {
        return;
      }

      if (state.payment) {
        this.paymentInfoBlock.set(this.getPaymentInfo(state.payment));
        this.durationInfoBlock.set(this.getDurationInfo(state.payment));

        if (state.payment?.startdate) {
          this.selectedStartDate.set(state.payment?.startdate);
        }

        //Bei Online-Payment ist die Adresse keine Pflichtangabe, weil sie aus Paypal ermittelt werden kann
        // this.needBillingAddress.set(state.payment.formOfPayment.paymentType !== 'ONLINEPAYMENT');
        this.scheduleOptionalBillingAddressValidityUpdate();
      }

      if (state.addresses?.billingAddress) {
        this.billingInfoBlock.set(this.getAddressInfo('billing', state));
        this.selectedBillingAddress.set(state.addresses?.billingAddress);
        this.patchBillingAddressForm(state.addresses.billingAddress);
      }

      if (state.addresses?.deliveryAddress) {
        this.deliveryInfoBlock.set(this.getAddressInfo('delivery', state));
        this.selectedDeliveryAddress.set(state.addresses?.deliveryAddress);
      }

      if (state.bonusData) {
        // Eigener Block: die Praemienadresse darf die Adresse des Nutzers in der Uebersicht nicht
        // ueberschreiben - sie ist nur das Ziel der Praemie, nicht seine Anschrift.
        //
        // Bewusst an bonusData statt an bonusAddress gehaengt: Bei einer Fremd- oder Geldpraemie
        // gibt es keine Praemienadresse des Bestellers. Der Block muss dann wieder verschwinden,
        // statt die Anschrift der zuvor gewaehlten Sachpraemie stehen zu lassen.
        this.bonusInfoBlock.set(this.getAddressInfo('bonus', state));
        this.selectedBonusAddress.set(state.bonusData.bonusAddress);
      }

      if (state.offerPrice?.length) {
        //price für die Darstellung holen
        this.offerPrice.set(state.offerPrice);
        this.priceInfoBlock.set(state.calculatingPrice ? '&nbsp;' : this.getPriceInfo(state));
      }

      this.lastOrderState = state;
      this.updateBillingAddressRequest(state);

      this.calculatingPrice.set(!!state.calculatingPrice);
    }));

    //Falls die Rechnungsadresse an dieser Stelle angegeben werden muss, schicken wir die Daten an den Service
    this.stateSubscriptions.add(this.orderProcessService.connectForm<AddressFormValue>(
      this.billingAddressFormGroup.get('billingAddress') as FormGroup,
      state => state,
      (state, value) => ({
        ...state,
        addresses: {
          ...state.addresses,
          billingAddress: value,
          deliveryAddress: this.isDigitalOffer() ? value : state.addresses?.deliveryAddress
        }
      })
    ));

    // Kein connectForm fuer die Lieferadresse: Der SummaryStep hat kein Lieferadress-Formular mehr,
    // seit die Lieferadresse hier nicht mehr aenderbar ist. Erfasst wird sie ausschliesslich im
    // Adress-Step, der sie von dort auch in den Bestell-State schreibt.

  }

  /**
   * Bei digitalen Abos gibt es keinen Adress-Step. Hat der Nutzer noch gar keine Adresse - weder
   * Liefer- noch Rechnungsadresse -, wird sie hier abgefragt: die Maske wird eingeblendet und die
   * Rechnungsadresse zur Pflichtangabe (s. scheduleOptionalBillingAddressValidityUpdate).
   *
   * Bei einer Online-Zahlart klappt die Maske NICHT von selbst auf: Dort liefert der Zahlungs-
   * anbieter die Rechnungsadresse, sie liegt aber erst nach der Zahlung vor. Wer sie vorher
   * aufklappt, fragt eine Adresse ab, die der Nutzer gleich darauf geschenkt bekommt. Der Stift
   * bleibt jederzeit verfuegbar; erforderlich (needBillingAddress) ist die Adresse trotzdem, damit
   * ohne Anschrift keine Bestellung abgeschickt werden kann - liefert der Anbieter keine, muss der
   * Nutzer sie ueber den Stift nachtragen.
   *
   * Liegt eine Adresse vor - egal woher: Bestandskunde, Zahlungsanbieter, Adress-Step -, bleibt die
   * Maske zu; sie oeffnet sich dann nur noch ueber den Stift. Der Stepper baut diesen Schritt sofort
   * mit auf, Adresse und Zahlart kommen erst spaeter an. Eine von selbst geoeffnete Maske wird deshalb
   * wieder zugeklappt, sobald eine Adresse oder eine Online-Zahlart feststeht.
   *
   * Nicht angefasst wird die Maske, die der Nutzer ueber den Stift geoeffnet oder in die er schon
   * etwas eingetippt hat: Sobald er das erste Zeichen tippt, gaebe es eine Adresse und die Maske
   * wuerde ihm sonst unter den Haenden verschwinden.
   */
  private updateBillingAddressRequest(state: OrderState): void {
    if (this.billingAddressAskedInAddressStep()) {
      // Der Adress-Step hat die Rechnungsadresse abgefragt. Hier ist sie damit weder Pflichtangabe
      // noch aenderbar - sonst haenge der Bestell-Button an einem Formular, das der Nutzer auf
      // dieser Seite gar nicht mehr zu sehen bekommt.
      this.needBillingAddress.set(false);
      if (this.showBillingAddressEditor()) {
        this.setBillingAddressEditorOpen(false);
      }
      this.scheduleOptionalBillingAddressValidityUpdate();
      return;
    }

    const hasAddress = hasOrderAddress(state.addresses);
    this.needBillingAddress.set(!hasAddress);

    if (!this.isBillingAddressEditable()) {
      // Bestandskunde ohne Online-Zahlart: weder Stift noch Maske - auch keine automatisch
      // geoeffnete, falls die Adresse erst spaeter im Bestell-State ankommt.
      if (this.showBillingAddressEditor()) {
        this.setBillingAddressEditorOpen(false);
      }
    } else if (!this.billingEditorOpenedByUser && !this.isBillingFormEditedByUser()) {
      const openAutomatically = !hasAddress && !this.showOnlinePayment();
      if (openAutomatically !== this.showBillingAddressEditor()) {
        this.setBillingAddressEditorOpen(openAutomatically);
      }
    }

    this.scheduleOptionalBillingAddressValidityUpdate();
  }

  /**
   * Digitales Abo mit Sachpraemie beim Neukunden: Die Rechnungsadresse wird mit der Praemienadresse
   * vorbefuellt.
   *
   * Ohne eigene Adresse klappt der Bonus-Step die Praemienadress-Maske sofort auf (s.
   * resolveBonusAddressTarget) - der Nutzer hat seine Anschrift dort also schon einmal erfasst.
   * Eine Sachpraemie bekommt der Besteller selbst; bonusData.bonusAddress gibt es nur fuer sie (s.
   * BonusStepComponent.publishBonusData). Hier wird die Anschrift nicht ein zweites Mal abgefragt,
   * sondern uebernommen: Sie steht als Rechnungsadresse in der Uebersicht und bleibt ueber den Stift
   * aenderbar. Als vom Nutzer erfasste Adresse hat sie auch Vorrang vor der eines Zahlungsanbieters
   * (s. mergeExternalAddress).
   *
   * Aendert der Nutzer danach die Praemienadresse, folgt die Rechnungsadresse - solange sie noch die
   * uebernommene ist. Hat er sie in der Maske selbst bearbeitet oder hat sie eine andere Quelle,
   * bleibt sie unangetastet.
   *
   * @return true, wenn die Rechnungsadresse in den Bestell-State geschrieben wurde.
   */
  private prefillBillingAddressFromBonus(state: OrderState): boolean {
    if (!this.isDigitalOffer() || !this.isBillingAddressEditable() || this.isBillingFormEditedByUser()) {
      return false;
    }

    const bonusAddress = state.bonusData?.bonusAddress;
    if (isAddressFormValueEmpty(bonusAddress)) {
      return false;
    }

    const prefillUntouched = !!this.billingAddressFromBonus
      && state.addresses?.billingAddress === this.billingAddressFromBonus;
    if (hasOrderAddress(state.addresses) && !prefillUntouched) {
      return false;
    }

    this.patchBillingAddressForm(bonusAddress);
    const billingAddress = this.billingAddressGroup.value as AddressFormValue;
    // Ohne diesen Vergleich schaukelten sich Bonus-Step und Uebersicht auf: Faellt die
    // Praemienadresse auf das Lieferziel zurueck, ist sie genau die hier geschriebene Adresse.
    // Verglichen wird nur der Inhalt: showAddress spiegelt die Maske, nicht die Anschrift.
    const content = (value: AddressFormValue) =>
      JSON.stringify([value?.person, value?.address, value?.userData]);
    if (content(billingAddress) === content(state.addresses?.billingAddress)) {
      return false;
    }

    this.billingAddressFromBonus = billingAddress;
    this.orderProcessService.update(current => ({
      ...current,
      addresses: {
        ...current.addresses,
        billingAddress,
        // Wie beim Formular selbst (connectForm): Bei digitalen Abos ist die Lieferadresse die
        // Rechnungsadresse.
        deliveryAddress: billingAddress
      }
    }));
    return true;
  }

  /**
   * Hat der Nutzer in der Maske selbst etwas eingetragen?
   *
   * Nicht am Inhalt ablesbar: Die Adresse des Zahlungsanbieters landet ueber den Bestell-State per
   * patchBillingAddressForm ebenfalls in der Maske - eine Inhaltspruefung hielte sie dann fuer eine
   * Eingabe des Nutzers und liesse die Maske offen. Programmatisches Befuellen macht das Formular
   * nicht dirty, erst eine Eingabe des Nutzers.
   */
  private isBillingFormEditedByUser(): boolean {
    return this.billingAddressGroup.dirty;
  }

  private setBillingAddressEditorOpen(open: boolean): void {
    this.showBillingAddressEditor.set(open);
    this.billingAddressGroup.get('showAddress')?.setValue(open, {emitEvent: false});
  }

  protected openBillingAddressEditor(): void {
    const nextState = !this.showBillingAddressEditor();
    // Ab jetzt gehoert der Zustand dem Nutzer: Das automatische Zuklappen bei Online-Zahlung
    // fasst die Maske nicht mehr an.
    this.billingEditorOpenedByUser = nextState;
    this.setBillingAddressEditorOpen(nextState);
    this.scheduleOptionalBillingAddressValidityUpdate();
  }

  private patchBillingAddressForm(billingAddress: any): void {
    this.patchAddressForm(this.billingAddressGroup, billingAddress);
  }

  private patchAddressForm(group: FormGroup, addressData: any): void {
    if (!addressData) {
      return;
    }

    group.patchValue({
      userData: {
        birthDay: addressData?.userData?.birthDay ?? '',
        phone: addressData?.userData?.phone ?? '',
        email: addressData?.userData?.email ?? ''
      },
      address: {
        country: addressData?.address?.country ?? '',
        zipcode: addressData?.address?.zipcode ?? '',
        city: addressData?.address?.city ?? '',
        street: addressData?.address?.street ?? '',
        houseNo: addressData?.address?.houseNo ?? addressData?.address?.houseno ?? '',
        extraLine: addressData?.address?.extraLine ?? '',
        houseNoExt: addressData?.address?.houseNoExt ?? '',
        stiege: addressData?.address?.stiege ?? '',
        stock: addressData?.address?.stock ?? '',
        tuer: addressData?.address?.tuer ?? '',
        district: addressData?.address?.district ?? ''
      },
      person: {
        title: addressData?.person?.title ?? '',
        salutation: addressData?.person?.salutation ?? '',
        firstName: addressData?.person?.firstName ?? addressData?.person?.firstname ?? '',
        lastName: addressData?.person?.lastName ?? addressData?.person?.lastname ?? '',
        initials: addressData?.person?.initials ?? '',
        middlename: addressData?.person?.middlename ?? ''
      }
    }, {emitEvent: false});
  }

  public getVisibleSummaryBlocks(): any[] {
    const blocks = [];

    // Digitale Abos werden nicht zugestellt: Der Lieferadress-Block entfaellt hier ganz. Im
    // Bestell-State steht als Lieferadresse die Rechnungsadresse - ohne diese Weiche stuende
    // dieselbe Anschrift zweimal in der Uebersicht.
    !this.isDigitalOffer() && this.hasAddressInfo('delivery') ? blocks.push('delivery') : null;
    this.hasAddressInfo('billing') ? blocks.push('billing') : null;
    this.hasAddressInfo('bonus') ? blocks.push('bonus') : null;
    blocks.push('payment', 'duration');
    this.offerPrice() ? blocks.push('price') : null;

    return blocks;
  }

  private hasAddressInfo(type: 'delivery' | 'billing' | 'bonus') {
    let addressInfo = '';

    switch (type) {
      case 'delivery':
        addressInfo = this.deliveryInfoBlock();
        break;
      case 'billing':
        addressInfo = this.billingInfoBlock();
        break;
      case 'bonus':
        addressInfo = this.bonusInfoBlock();
        break;
    }

    return !!addressInfo;
  }

  public getBlockTitle(type: string): string {
    // @formatter:off
    switch (type) {
      case 'delivery': return this.translationService.getTranslation('address_delivery');
      case 'billing': return this.translationService.getTranslation('address_billing');
      case 'bonus': return this.translationService.getTranslation(
        bonusAddressTranslationKey(this.offer()?.offerType, this.bonusAddressIsDeliveryAddress()));
      case 'payment': return this.translationService.getTranslation('payment_rhythm');
      case 'duration': return this.translationService.getTranslation('step_orderDateAndOptins');
      case 'price': return this.translationService.getTranslation('price');
      default: return '';
    }
    // @formatter:on
  }

  public getBlockContent(type: string): SafeHtml {
    let content: string;
    // @formatter:off
    switch (type) {
      case 'delivery': content = this.deliveryInfoBlock(); break;
      case 'billing': content = this.billingInfoBlock(); break;
      case 'bonus': content = this.bonusInfoBlock(); break;
      case 'payment': content = this.paymentInfoBlock(); break;
      case 'duration': content = this.durationInfoBlock(); break;
      case 'price': content = this.priceInfoBlock(); break;
      default: content = ''; break;
    }
    // @formatter:on

    return content;
  }

  private getPaymentInfo(payment: any) {
    return [
      payment.formOfPayment.description + ' (' + payment.paymentFrequency.description + ')',
      !!payment.iban ? 'IBAN ' + this.bankAccountPipe.transform(payment.iban) : '',
      !!payment.bic && payment.bic.trim().match(/^[A-Z\d]+$/i) ? 'BIC ' + payment.bic : ''
    ].join('<br>')
      .replace(/undefined/g, '') // remove 'undefined'
      .replace(/(<br>){2,}/g, '<br>') // replace empty lines
      .replace(/^<br>/, ''); // remove a leading empty line
  }

  private getDurationInfo(payment: any) {
    return [
      this.translationService.getTranslation('startdate'),
      this.datePipe.transform(payment.startdate, 'dd.MM.yyyy')
    ].join(' ')
      .trim()
      .replace(/undefined/g, ''); // remove 'undefined'
  }

  private getPriceInfo(state: OrderState) {
    let priceInfo = [];
    state.offerPrice?.forEach(price => {
      if (!!price) {
        priceInfo.push(price.price && price.currency
          ? this.decimalPipe.transform(price.price, '1.2-2') + ' ' + price.currency
          : price.priceText);
        !!price.porto
          ? priceInfo.push(this.translationService.getTranslation('price_delivery') + ' ' + this.decimalPipe.transform(price.price, '1.2-2') + ' ' + price.currency)
          : null;
      }
    });

    return priceInfo.join('<br>')
      .replace(/undefined/g, '') // remove 'undefined'
      .replace(/^<br>/, ''); // remove a leading empty line
  }

  private getAddressInfo(type: 'delivery' | 'billing' | 'bonus', state?: OrderState) {
    let person = state?.addresses?.billingAddress?.person;
    let userData = state.addresses?.billingAddress?.userData;
    let address = state?.addresses?.billingAddress?.address;

    switch (type) {
      case 'delivery':
        person = state?.addresses?.deliveryAddress?.person;
        userData = state?.addresses?.deliveryAddress?.userData;
        address = state?.addresses?.deliveryAddress?.address;
        break;
      case 'bonus':
        person = state?.bonusData?.bonusAddress?.person;
        userData = state?.bonusData?.bonusAddress?.userData;
        address = state?.bonusData?.bonusAddress?.address;
        break;

      default:
        ///Sonst bleiben die Werte einfach unverändert (billing address)
        break;
    }

    const personIsEmpty = person != null && Object.values(person).every(value => typeof value == 'object' || value === '');
    const userDataIsEmpty = userData != null && ['phonecode', 'phone', 'email'].every(key => (userData[key] ?? '') === '');
    const addressIsEmpty = address != null && Object.values(address).every(value => typeof value == 'object' || value === '');

    if (personIsEmpty && userDataIsEmpty && addressIsEmpty) {
      return '';
    }

    const phoneCode = (userData?.phonecode ?? '').toString().trim();
    let phone = (userData?.phone ?? '').toString().trim();
    if (phoneCode && phone.startsWith(phoneCode)) {
      phone = phone.slice(phoneCode.length).trim();
    }
    const phoneLine = ((phoneCode ? phoneCode + ' ' : '') + phone).trim();

    let ret = [
      (person?.salutation?.description + ' ' + (person?.title?.description ?? '') + ' ' + (person?.firstname ?? person?.firstName ?? '') + ' ' + (person?.lastname ?? person?.lastName ?? '')).trim(),
      (address?.street + ' ' + this.houseNoPipe.transform(address?.houseno ?? address?.houseNo)).trim(),
      (address?.zipcode + ' ' + address?.city).trim(),
      phoneLine,
      userData?.email,
    ].join('<br>')
      .replace(/undefined/g, '') // remove 'undefined'
      .replace(/ {2,}/g, ' ') // remove multi-space
      .replace(/( ?<br>){2,}/g, '<br>') // remove empty lines
      .replace(/null/g, '') //remove 'null'
      .replace(/^<br>/, ''); // remove a leading empty line
    return ret;
  }

  onCompleteOrder(orderResult: OrderResultModel): void {
    this.creatingOrder.set(true);
    // Nach oben scrollen, damit man den Spinner und die Fehlermeldungen sieht!
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.errorsArr = [];
    const data = this.orderProcessService.buildOrderDto();
    if (!data?.email?.match(ValidationPattern.EMAIL)) {
      console.error(`invalid email '${data?.email}'`)
      return;
    }

    if (!this.userData() || this.clientNoUrl()) {
      this.subscriptionService.sendOrderUnauthorized(data).subscribe({
        next: (res) => {
          this.orderTrackingService.pushEvent(GtmEnum.PURCHASED, res.body);
          this.doUpScoreTracking();
          const otlcCode = res.otlc ?? '';
          if (otlcCode.length) {
            this.loginService.loginByOtlc(otlcCode);
          }
          // Result an der Session speichern, da wir nichts nachladen können -> User ist noch nicht da ^^
          this.creatingOrder.set(false);
          const id: number = res.body.webId;
          /* falls direkt nach Paradise geschrieben wird, ist die backendId > 0 und die webId = 0. => Das Abo kann nicht geladen werden und die Bestellbestätigungsseite ist leer.
         Es muss also möglich sein, aus beiden Quellen das Abo zu laden */
          const backendId: string = res.body.backendId > 0 ? res.body.backendId.toString() : '-1';
          this.subscriptionService.setPriceForKey(id, this.offerPrice());
          this.navigationService.navigateTo(NavigationCommand.OFFERORDERED, [id.toString(), backendId, data.orderChoiceKey]).then((navigated) => {
            if (navigated) {
              this.snackBar.openSnackbar(this.translationService.getTranslation('saved'));
            }
          });
        },
        error: (error) => {
          // const errTestObj = {};  // Warning, wenn einkommentiert
          this.creatingOrder.set(false);
          console.error(error);
          //this.errorService.handleAPIError([this.firstFormGroup, this.secondFormGroup, this.thirdFormGroup, this.fourthFormGroup, this.fifthFormGroup], errTestObj, this.errorsArr, stepper);
        }
      });
    } else {
      this.subscriptionService.sendOrder(data).subscribe({
        next: (res) => {
          this.orderTrackingService.pushEvent(GtmEnum.PURCHASED, res);
          this.doUpScoreTracking();
          this.creatingOrder.set(false);
          const id: number = res.backendId > 0 ? res.backendId : res.webId;
          this.subscriptionService.setPriceForKey(id, this.offerPrice());
          this.navigationService.navigateTo(NavigationCommand.OFFERORDERED, [res.webId, res.backendId, data.orderChoiceKey]);
        },
        error: (error) => {
          this.creatingOrder.set(false);
          // const errTestObj = {};  // Warning, wenn einkommentiert
          console.error(error);
          // this.errorService.handleAPIError(fusingTest, error.error, this.errorsArr);
          // this.errorService.handleAPIError([this.complaintForm, this.complaintCompensationForm], error.error, this.errorsArr);
          //this.errorService.handleAPIError([this.firstFormGroup, this.secondFormGroup, this.thirdFormGroup, this.fourthFormGroup, this.fifthFormGroup], errTestObj, this.errorsArr, stepper);
        }
      });
    }
  }

  public isLastBlockFullWidth(index: number): boolean {
    const blocks = this.getVisibleSummaryBlocks();
    return blocks.length % 2 === 1 && index === blocks.length - 1;
  }

  /**
   * triggert UpScore
   *
   * Wenn offer.tracking.upScore.domain nicht leer ist, wird UpScore ausgeführt.
   * Der UpScore-Parameter "object_id" wird in den Site-URL-Suchparametern unter dem (Konfig-)Key "offer.tracking.upScore.objectIdKey" gesucht.
   * Der UpScore-Parameter "url" wird in den Site-URL-Suchparametern unter dem (Konfig-)Key "offer.tracking.upScore.urlKey" gesucht.
   */
  async doUpScoreTracking(): Promise<void> {
    const urlParamMap = Object.fromEntries(new URLSearchParams(window.location.search));

    const executorFn = () => {
      // Parameter-Keys bestimmen
      let objectIdKey = this.configService.getConfig('offer.tracking.upScore.objectIdKey')?.value;
      // set Default-Values, falls leer
      objectIdKey = !!objectIdKey ? objectIdKey : 'upScoreId';
      // Parameterwert ermitteln
      let objectId = urlParamMap[objectIdKey] ?? '';

      const data = {object_id: objectId, object_type: 'article', conversion: 2};

      let p;

      if (upScoreVersion == 'v3') {
        p = {
          config: {domain: upScoreDomain, article: 'article', track_positions: true},
          data: data
        };
      } else {
        p = data;
      }

      console.log('%cUpScore-Parameter: ', 'color:orange;', p);

      // @ts-ignore
      upScoreVersion == 'v3' ? upScore(p) : upScore('page_view', p);
    };

    let upScoreDomain = this.configService.getConfig('offer.tracking.upScore.domain')?.value;
    let upScoreVersion = this.configService.getConfig('offer.tracking.upScore.version')?.value ?? 'v4';
    // P5W-3521: SDZC | Anbindung UpScore API für Conversion-Messung
    // mehrere Portale leiten zu einem WebAbo-Shop
    //   1) die Domain wird in diesem Fall als URL-Parameter übergeben (Param:urlParamName), oder
    //   2) ggf. in einem "Sub"-Parameter dieses Parameters (Param:urlParamName->subParamName)
    if (upScoreDomain.startsWith('Param:')) {
      let upScoreDomainConf = upScoreDomain.substring('Param:'.length).split('->');
      let upScoreDomainByParam = urlParamMap[upScoreDomainConf[0]];
      // Falls Parameter im Parameterwert zu finden ist, ...
      if (!!upScoreDomainConf[1]) {
        // ... 1st-Level Parameter dekodieren, ...
        upScoreDomainByParam = decodeURIComponent(upScoreDomainByParam);
        // ... die URL-Parameter ermitteln, ...
        const secondLevelUrlParams = Object.fromEntries(new URLSearchParams(upScoreDomainByParam.split('?').pop()));
        // ... den eingebetteten 2nd-Level Parameter auslesen und schließlich ...
        upScoreDomainByParam = secondLevelUrlParams[upScoreDomainConf[1]] ?? null;
        // ... den Domain-/Hostnamen ermitteln
        try {
          // a) komplette URL
          upScoreDomainByParam = new URL(upScoreDomainByParam).hostname;
        } catch (e) {
          // b) nur Hostname
          try {
            upScoreDomainByParam = new URL('https://' + upScoreDomainByParam).hostname;
          } catch (e) {
            // c) leider keins von beiden
            console.error('UpScore-Domain kann nicht ermittelt werden aus URL: ' + e.message);
            upScoreDomainByParam = null;
          }
        }
      }
      upScoreDomain = !!upScoreDomainByParam ? upScoreDomainByParam : null;
      // nur sld + tld ausgeben
      if (!!upScoreDomain) {
        upScoreDomain = upScoreDomain.split('.').slice(-2).join('.');
      }
    }

    if (!!upScoreDomain) {
      // @ts-ignore
      if (typeof upScore === 'function') {
        executorFn();
      } else {
        const possibleUpScoreLocations = {
          v3: 'https://files.upscore.com/async/upScore.js',  // v3
          v4: 'https://files.upscore.com/site_script/' + upScoreDomain + '.js' // v4
        };

        const scriptUrl = possibleUpScoreLocations[upScoreVersion];

        try {
          await this.scriptLoader.loadScript(scriptUrl);
          // @ts-ignore
          if (typeof upScore === 'function') {
            executorFn();
            return;
          } else {
            console.error('UpScore-API nicht gefunden (' + scriptUrl + ')');
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  }

  get billingAddressGroup() {
    return this.billingAddressFormGroup.get('billingAddress') as FormGroup;
  }

  buildAddressGroups() {
    this.billingAddressFormGroup = this.formBuilder.group({
      billingAddress: this.createAddressGroup(),
    });
  }

  private createAddressGroup(): FormGroup {
    return this.formBuilder.group({
      userData: this.formBuilder.group({
        birthDay: [''],
        phone: [''],
        email: ['']
      }),
      showAddress: [''],
      address: this.formBuilder.group({
        country: [''],
        zipcode: [''],
        city: [''],
        street: [''],
        houseNo: [''],
        extraLine: [''],
        houseNoExt: [''],
        stiege: [''],
        stock: [''],
        tuer: [''],
        district: ['']
      }),
      person: this.formBuilder.group({
        title: [''],
        salutation: [''],
        firstName: [''],
        lastName: [''],
        initials: [''],
        middlename: [''],
      })
    });
  }
}
